// src/api/ApiClient.js
import axios from 'axios';
import Swal from 'sweetalert2';
import { redirectToSessionExpired } from '../utils/session';

export class ApiClient {
  constructor(baseURL) {
    this.client = axios.create({
      baseURL,
    });

    // Add interceptors if needed
    this.client.interceptors.request.use((config) => {
      if (!config) return config;
      config.headers = config.headers ?? {};
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      // Only set JSON content-type for non-FormData requests.
      // FormData requests must NOT have Content-Type set manually so the
      // browser can inject the correct multipart boundary automatically.
      const isFormData = config.data instanceof FormData;
      if (!isFormData && !config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
      if (isFormData) {
        delete config.headers['Content-Type'];
      }
      config.headers['Accept'] = 'application/json';
      return config;
    });
  }

  request(config) {
    return this.client(config)
      .then((response) => response.data)
      .catch((error) => {
        const status = error.response?.status;

        if (status === 401) {
          error.handled = true;
          redirectToSessionExpired('Your session has expired. Please login again.');
          throw error;
        }

        // Opt-out for calls where a non-2xx response is an expected, normal
        // outcome (e.g. "no donor attached yet") rather than a real failure —
        // the error still rejects normally, callers just handle it locally
        // instead of a global popup interrupting them.
        if (config.silent) {
          error.handled = true;
          throw error;
        }

        if (status === 403) {
          // User doesn't have permission
          error.handled = true;
          Swal.fire({
            icon: 'error',
            title: 'Access Denied',
            text: 'You do not have permission to perform this action. Please contact your administrator.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#d33',
          });
          throw error;
        }

        // All other errors — show a generic notification with the backend message
        const message =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Something went wrong. Please try again.';
        error.handled = true;
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: message,
          confirmButtonText: 'OK',
        });
        throw error;
      });
  }

  get(url, config = {}) {
    return this.request({
      method: "GET",
      url,
      ...config,
    });
  }

  put(url, data, config = {}) {
    return this.request({
      method: "PUT",
      url,
      data,
      ...config,
    });
  }

  patch(url, data, config = {}) {
    return this.request({
      method: "PATCH",
      url,
      data,
      ...config,
    });
  }

  delete(url, config = {}) {
    return this.request({
      method: "DELETE",
      url,
      ...config,
    });
  }
  post(url, data, config = {}) {
    console.log('config.headers', config.headers);
    return this.request({
      method: "POST",
      url,
      data,
      ...config,
    });
  }
}
